import React, { useState, useEffect, useRef } from 'react';
import { flexRender, type Table, type ColumnDef } from '@tanstack/react-table';
import { Virtuoso } from 'react-virtuoso';
import { ChevronRight, ChevronDown } from 'lucide-react';
import CellWithTooltip from './CellWithTooltip';
import type { DataTableColumn, RowBgColorResult } from '../types';
import { getCommonPinningStyles } from "./commonStyles";
import { resolveRowBgColor } from "../rowBgColor";

interface DataTableBodyProps<TData> {
  table: Table<TData>;
  columns: ColumnDef<TData>[];
  emptyMessage?: string;
  enableVirtualization?: boolean;
  enableColumnResizing?: boolean;
  rowHeight?: number;
  overscan?: number;
  parentRef?: React.RefObject<HTMLDivElement>;
  rowClickable?: boolean;
  onRowClick?: (row: TData, columnId: string) => void;
  hideLeafRows?: boolean;
  gridTemplateColumns: string;
  rowBgColor?: (row: TData) => RowBgColorResult | undefined;
  condensed?: boolean;
}

export default function DataTableBody<TData>({
  table,
  columns,
  emptyMessage = 'No data available',
  enableVirtualization = false,
  enableColumnResizing = false,
  rowHeight = 52,
  overscan = 200,
  parentRef,
  rowClickable = false,
  onRowClick,
  hideLeafRows = false,
  gridTemplateColumns,
  rowBgColor,
  condensed = false,
}: DataTableBodyProps<TData>) {
  const allRows = table.getRowModel().rows;
  const rows = hideLeafRows ? allRows.filter(row => row.getIsGrouped()) : allRows;
  const grouping = table.getState().grouping;

  // Virtuoso requires a real HTMLElement for customScrollParent, not a ref.
  // We track it via state so the Virtuoso component re-renders once it's available.
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (parentRef?.current && parentRef.current !== scrollParent) {
      setScrollParent(parentRef.current);
    }
  }, [parentRef?.current]);

  const totalColumnsWidth = table
    .getVisibleLeafColumns()
    .reduce((sum, col) => sum + col.getSize(), 0);

  const getRowClassName = (row: any) => {
    const depth = row.depth;
    if (depth === 0 && row.getIsGrouped()) {
      return 'bg-gray-50 font-medium border-t-2 border-gray-300';
    } else if (depth === 1 && row.getIsGrouped()) {
      return 'bg-gray-100/50 font-medium';
    } else if (row.getIsGrouped()) {
      return 'bg-gray-100/30';
    }
    return 'hover:bg-gray-50';
  };

  const getCellPadding = (depth: number, isFirstCell: boolean) => {
    if (!isFirstCell) return '';
    const basePadding = 24;
    const indentPerLevel = 20;
    const totalPadding = basePadding + (depth * indentPerLevel);
    return `${totalPadding}px`;
  };

  const handleCellClick = (row: any, column: DataTableColumn<TData>, columnId: string) => {
    if (row.getIsGrouped()) return;
    if (column.disableRowClick) return;
    if (column.onCellClick) {
      column.onCellClick(row.original, column);
      return;
    }
    if (rowClickable && onRowClick) {
      onRowClick(row.original, columnId);
    }
  };

  const getCellClickableClass = (column: DataTableColumn<TData>, row: any) => {
    if (row.getIsGrouped()) return '';
    if (column.disableRowClick) return '';
    if (column.onCellClick || (rowClickable && onRowClick)) {
      return 'cursor-pointer hover:bg-gray-100';
    }
    return '';
  };

  const renderRow = (row: any, index?: number) => {
    const rowStyle: React.CSSProperties = {
      display: 'grid',
      gridTemplateColumns,
    };

    if (enableVirtualization && rowHeight) {
      rowStyle.height = rowHeight;
      rowStyle.overflowY = 'clip';
    }

    // Resolve custom row background color once per row
    const resolvedBgColor = (rowBgColor)
      ? resolveRowBgColor(rowBgColor(row.original))
      : undefined;

    if (resolvedBgColor) {
      rowStyle.backgroundColor = resolvedBgColor;
    }

    return (
      <div
        key={row.id}
        className={`border-b border-gray-200 ${getRowClassName(row)}`}
        style={rowStyle}
        data-testid={row.getIsGrouped() ? `group-row-${row.id}` : `data-row-${row.id}`}
        data-index={index}
      >
        {row.getVisibleCells().map((cell: any, cellIndex: number) => {
          const isFirstCell = cellIndex === 0;
          const paddingLeft = grouping.length > 0 ? getCellPadding(row.depth, isFirstCell) : undefined;

          const columnDef = cell.column.columnDef as DataTableColumn<TData>;
          const isClickable = !row.getIsGrouped() && !columnDef.disableRowClick &&
            (columnDef.onCellClick || (rowClickable && onRowClick));

          const cellStyle: React.CSSProperties = {
            paddingLeft: paddingLeft || (condensed ? '12px' : '24px'),
            paddingRight: condensed ? '12px' : '24px',
            overflow: 'hidden',
            ...getCommonPinningStyles(cell.column, resolvedBgColor),
          };

          return (
            <div
              key={cell.id}
              className={`${condensed ? 'py-1' : 'py-3'} text-sm text-gray-900 flex items-center ${getCellClickableClass(columnDef, row)}`}
              style={cellStyle}
              onClick={isClickable ? () => handleCellClick(row, columnDef, cell.column.id) : undefined}
            >
              {cell.getIsGrouped() ? (
                <div className="flex items-center gap-2">
                  {!(hideLeafRows && row.depth === grouping.length - 1) && (
                    <button
                      onClick={row.getToggleExpandedHandler()}
                      className="hover:bg-gray-300 p-0.5 rounded"
                    >
                      {row.getIsExpanded() ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <span className="font-semibold">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </span>
                  <span className="text-gray-500 text-xs">
                    ({row.subRows.length})
                  </span>
                </div>
              ) : cell.getIsAggregated() ? (
                <span
                  className="font-medium text-gray-700"
                  data-testid={`aggregated-cell-${cell.column.id}`}
                >
                  {flexRender(
                    cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                    cell.getContext()
                  )}
                </span>
              ) : cell.getIsPlaceholder() ? (
                <span className="text-gray-400">—</span>
              ) : (
                <CellWithTooltip
                  content={flexRender(cell.column.columnDef.cell, cell.getContext())}
                  minSize={(cell.column.columnDef as DataTableColumn<TData>).minSize}
                  maxSize={(cell.column.columnDef as DataTableColumn<TData>).maxSize}
                  enableTooltip={(cell.column.columnDef as DataTableColumn<TData>).enableTooltip}
                  condensed={condensed}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (rows.length === 0) {
    return (
      <div
        style={{ display: 'grid', gridTemplateColumns }}
        className="bg-white"
      >
        <div
          style={{ gridColumn: `1 / -1` }}
          className="px-6 py-4 text-center text-gray-500"
        >
          {emptyMessage}
        </div>
      </div>
    );
  }

  if (enableVirtualization && scrollParent) {
    return (
      <div className="bg-white" style={{ minWidth: totalColumnsWidth }}>
        <Virtuoso
          customScrollParent={scrollParent}
          totalCount={rows.length}
          fixedItemHeight={rowHeight}
          increaseViewportBy={overscan * rowHeight}
          computeItemKey={(index) => rows[index]?.id ?? index}
          itemContent={(index) => renderRow(rows[index], index)}
        />
      </div>
    );
  }

  // Show a loading placeholder while waiting for the scroll parent to mount
  // so the non-virtualized path doesn't flash during the first effect cycle.
  if (enableVirtualization && !scrollParent) {
    return (
      <div
        style={{ display: 'grid', gridTemplateColumns }}
        className="bg-white"
      >
        <div style={{ gridColumn: `1 / -1` }} className="px-6 py-4 text-center text-gray-500">
          Loading
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white divide-y divide-gray-200" style={{ minWidth: 'fit-content' }}>
      {rows.map((row, index) => renderRow(row, index))}
    </div>
  );
}
